export function onlyLetters(s: string): string {
  return s.replace(/[^\p{L}\p{N}]/gu, '').toLowerCase();
}

export interface QuoteMatchOptions {
  charStartHint?: number | null;
  wordBefore?: string | null;
  wordAfter?: string | null;
  occurrenceIndex?: number | null;
}

function findByOccurrence(strippedQuote: string, strippedPage: string, occurrenceIndex: number): number {
  let count = 0;
  let pos = -1;
  while (count <= occurrenceIndex) {
    pos = strippedPage.indexOf(strippedQuote, pos + 1);
    if (pos === -1) break;
    count++;
  }
  return pos;
}

function findByContext(
  strippedQuote: string,
  strippedPage: string,
  wordBefore?: string | null,
  wordAfter?: string | null,
): number {
  const cb = wordBefore ? onlyLetters(wordBefore) : '';
  const ca = wordAfter ? onlyLetters(wordAfter) : '';
  const searchCtx = (needle: string): number => {
    const idx = strippedPage.indexOf(needle);
    return idx !== -1 && needle.startsWith(cb) ? idx + cb.length : idx;
  };
  const ctxIdx = searchCtx(cb + strippedQuote + ca);
  if (ctxIdx !== -1) return ctxIdx;
  if (cb) {
    const idx = searchCtx(cb + strippedQuote);
    if (idx !== -1) return idx;
  }
  return -1;
}

/**
 * findQuoteInPage locates a stripped quote within stripped page text.
 * Uses optional hints (charStartHint, wordBefore, wordAfter, occurrenceIndex)
 * to disambiguate when the same quote appears in multiple places.
 */
export function findQuoteInPage(
  strippedQuote: string,
  strippedPage: string,
  pageText: string,
  opts?: QuoteMatchOptions,
): number {
  if (opts?.occurrenceIndex != null && opts.occurrenceIndex > 0) {
    const pos = findByOccurrence(strippedQuote, strippedPage, opts.occurrenceIndex);
    if (pos !== -1) return pos;
  }

  if (opts?.charStartHint != null && opts.charStartHint >= 0) {
    const approxJsIdx = Math.min(opts.charStartHint, pageText.length - 1);
    let strippedBefore = 0;
    for (let i = 0; i < approxJsIdx && i < pageText.length; i++) {
      if (/\p{L}|\p{N}/u.test(pageText[i])) strippedBefore++;
    }
    const idx = strippedPage.indexOf(strippedQuote, Math.max(0, strippedBefore - 20));
    if (idx !== -1) return idx;
  }

  if (opts?.wordBefore || opts?.wordAfter) {
    const idx = findByContext(strippedQuote, strippedPage, opts.wordBefore, opts.wordAfter);
    if (idx !== -1) return idx;
  }

  return strippedPage.indexOf(strippedQuote);
}

/**
 * matchQuoteToPositions finds the character positions that correspond to a
 * quote within the page text by walking the page text and pdfium char array
 * in strict lockstep. Returns an array of matched char bounding boxes.
 */
export function matchQuoteToPositions(
  quoteText: string,
  pageText: string,
  chars: ReadonlyArray<{ x: number; y: number; width: number; height: number }>,
  opts?: QuoteMatchOptions,
): Array<{ x: number; y: number; width: number; height: number }> {
  const strippedQuote = onlyLetters(quoteText);
  const strippedPage = onlyLetters(pageText);
  if (strippedQuote.length < 3 || strippedPage.length < 3) return [];

  const matchIdx = findQuoteInPage(strippedQuote, strippedPage, pageText, opts);
  if (matchIdx === -1) return [];

  const matchedPositions: Array<{ x: number; y: number; width: number; height: number }> = [];
  let remaining = strippedQuote.length;
  let charIdx = 0;
  let strippedPos = 0;
  let collecting = false;

  for (let i = 0; i < pageText.length && remaining > 0 && charIdx < chars.length; i++) {
    const isLetter = /\p{L}|\p{N}/u.test(pageText[i]);
    if (isLetter) {
      if (!collecting) {
        if (strippedPos === matchIdx) collecting = true;
        strippedPos++;
      }
    }
    if (collecting && isLetter) {
      const ch = chars[charIdx];
      if (ch) matchedPositions.push({ x: ch.x, y: ch.y, width: ch.width, height: ch.height });
      remaining--;
    }
    charIdx++;
  }

  return matchedPositions;
}

/**
 * mergeCharRects groups individual character bounding boxes into line-width
 * rectangles by merging adjacent chars on the same Y baseline.
 */
export function mergeCharRects(
  charRects: ReadonlyArray<{ x: number; y: number; width: number; height: number }>,
): Array<{ x: number; y: number; width: number; height: number }> {
  if (charRects.length === 0) return [];

  const LINE_THRESHOLD = 0.6;
  const lines: Array<Array<{ x: number; y: number; width: number; height: number }>> = [];

  for (const r of charRects) {
    let found = false;
    for (const line of lines) {
      const anchorCenter = line[0].y + line[0].height / 2;
      const charCenter = r.y + r.height / 2;
      const avgHeight = (line[0].height + r.height) / 2;
      if (Math.abs(anchorCenter - charCenter) < LINE_THRESHOLD * avgHeight) {
        line.push(r);
        found = true;
        break;
      }
    }
    if (!found) {
      lines.push([r]);
    }
  }

  // Merge lines whose vertical bounds overlap (handles pdfium noise
  // that puts a stray char just outside the grouping threshold).
  for (let i = 0; i < lines.length; i++) {
    for (let j = i + 1; j < lines.length; j++) {
      const aY = lines[i][0].y;
      const aTop = lines[i].reduce((t, r) => Math.max(t, r.y + r.height), -Infinity);
      const bY = lines[j][0].y;
      const bTop = lines[j].reduce((t, r) => Math.max(t, r.y + r.height), -Infinity);
      if (aY < bTop && bY < aTop) {
        lines[i].push(...lines[j]);
        lines.splice(j, 1);
        j--;
      }
    }
  }

  return lines.map(line => {
    // Filter out height outliers (e.g. pdfium position noise for some chars)
    // that would make the merged rect extend far above/below the actual text.
    const heights = [...line].sort((a, b) => a.height - b.height);
    const median = heights[Math.floor(heights.length / 2)]?.height ?? 0;
    const filtered = median > 0 ? line.filter(r => r.height <= median * 1.5) : line;

    if (filtered.length === 0) {
      // All chars filtered, fall back to original
      filtered.push(...line);
    }

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxTop = -Infinity;
    for (const r of filtered) {
      if (r.x < minX) minX = r.x;
      if (r.x + r.width > maxX) maxX = r.x + r.width;
      if (r.y < minY) minY = r.y;
      const charTop = r.y + r.height;
      if (charTop > maxTop) maxTop = charTop;
    }
    return { x: minX, y: minY, width: maxX - minX, height: maxTop - minY };
  });
}

/**
 * flipYFromPdfium converts a bounding box from pdfium's bottom-left origin
 * (Y = distance from page bottom) to top-left origin for browser DOM.
 */
export function flipYFromPdfium(
  rect: { x: number; y: number; width: number; height: number },
  pageHeight: number,
): { x: number; y: number; width: number; height: number } {
  return {
    x: rect.x,
    y: pageHeight - rect.y - rect.height,
    width: rect.width,
    height: rect.height,
  };
}
